import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertCircle, UserCircle, MessageSquare, Newspaper, Calendar,
  ArrowRight, Clock, X, Check, Send, ExternalLink, ShieldCheck, TrendingUp,
  AlertTriangle, Sparkles, Eye, Globe, Briefcase, FileText, Phone, ListChecks, KeyRound,
  LifeBuoy, Plus, ChevronLeft, Loader2,
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import ChipInput from '../components/ChipInput';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import CertificationsField from '../components/CertificationsField';
import UserManual from '../components/UserManual';
import SupportThread from '../components/SupportThread';
import { useTranslation } from '../i18nContext';
import { useAuth } from '../lib/AuthContext';
import { usePublicConfig } from '../lib/publicConfig';


const FIELD_LABELS_ES = {
  bio: 'Biografía',
  photo_url: 'Foto',
  city: 'Ciudad',
  phone: 'Teléfono',
  additional_emails: 'Correos adicionales',
  additional_phones: 'Teléfonos adicionales',
  linkedin_url: 'LinkedIn',
  website: 'Página web',
  language_pairs: 'Pares de idiomas',
  references_text: 'Referencias',
  specialty: 'Especialidad',
  active_languages: 'Idiomas activos',
  passive_languages: 'Idiomas pasivos',
  modalities: 'Modalidades de interpretación',
};

function formatFieldNames(keys) {
  if (!keys || keys.length === 0) return '';
  const names = keys.map((k) => FIELD_LABELS_ES[k] || k);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} y ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
}

// `specialty` can be an array (from the API) or a comma-separated string (after
// the user edits the input). Count distinct non-empty values in both cases.
function countSpecialties(v) {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean).length;
  return String(v || '').split(',').map((s) => s.trim()).filter(Boolean).length;
}


export default function InterpreterDashboard() {
  const t = useTranslation();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // overview | edit | requests
  const [lastSaveResult, setLastSaveResult] = useState(null);
  const [supportUnread, setSupportUnread] = useState(0);
  const publicCfg = usePublicConfig();
  const showDues = !!(publicCfg && publicCfg.show_dues_on_profile);
  const maintenance = !!(publicCfg && publicCfg.maintenance_mode);
  const maxSpecialties = Number(publicCfg?.max_specialties || 0);

    async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/me/dashboard');
      setDash(data);
      const it = data.interpreter || {};
      const parts = (it.full_name || '').trim().split(/\s+/).filter(Boolean);
      setForm({
        nombres: it.nombres || parts[0] || '',
        apellido_paterno: it.apellido_paterno || parts[1] || '',
        apellido_materno: it.apellido_materno || parts.slice(2).join(' ') || '',
        bio: it.bio || '',
        photo_url: it.photo_url || '',
        specialty: Array.isArray(it.specialty)
          ? it.specialty
          : (it.specialty ? String(it.specialty).split(',').map((s) => s.trim()).filter(Boolean) : []),
        city: it.city || '',
        phone: it.phone || '',
        email: it.email || '',
        linkedin_url: it.linkedin_url || '',
        website: it.website || '',
        active_languages: it.active_languages || [],
        passive_languages: it.passive_languages || [],
        modalities: Array.isArray(it.modalities) ? it.modalities : [],
        additional_emails: Array.isArray(it.additional_emails) ? it.additional_emails : [],
        additional_phones: Array.isArray(it.additional_phones) ? it.additional_phones : [],
        references_text: it.references_text || '',
      });
    } catch { setDash(null); }
    try {
      const { data: reqs } = await api.get('/me/profile-edits');
      setRequests(reqs || []);
    } catch { /* noop */ }
    setLoading(false);
  }

  const refreshSupportUnread = () => {
    api.get('/support/my-unread-count').then(({ data }) => setSupportUnread(data?.count || 0)).catch(() => {});
  };
  useEffect(() => { load(); refreshSupportUnread(); }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setStatus('idle'); setError('');
    const it = dash?.interpreter || {};
    const payload = {};
    // Name parts (editable, auto-applied). Compare against the same fallback
    // baseline used to seed the form so untouched values aren't flagged.
    const nameParts = (it.full_name || '').trim().split(/\s+/).filter(Boolean);
    const baseNombres = it.nombres || nameParts[0] || '';
    const baseApPat = it.apellido_paterno || nameParts[1] || '';
    const baseApMat = it.apellido_materno || nameParts.slice(2).join(' ') || '';
    if ((form.nombres || '') !== baseNombres) payload.nombres = (form.nombres || '').trim();
    if ((form.apellido_paterno || '') !== baseApPat) payload.apellido_paterno = (form.apellido_paterno || '').trim();
    if ((form.apellido_materno || '') !== baseApMat) payload.apellido_materno = (form.apellido_materno || '').trim();
    if (form.bio !== (it.bio || '')) payload.bio = form.bio;
    if (form.photo_url !== (it.photo_url || '')) payload.photo_url = form.photo_url;
    const curSpec = Array.isArray(it.specialty)
      ? it.specialty
      : (it.specialty ? String(it.specialty).split(',').map((s) => s.trim()).filter(Boolean) : []);
    if (JSON.stringify(form.specialty || []) !== JSON.stringify(curSpec)) payload.specialty = form.specialty || [];
    if (form.city !== (it.city || '')) payload.city = form.city;
    if (form.phone !== (it.phone || '')) payload.phone = form.phone;
    if ((form.email || '') !== (it.email || '')) payload.email = (form.email || '').trim();
    if (JSON.stringify(form.additional_emails || []) !== JSON.stringify(it.additional_emails || [])) {
      payload.additional_emails = form.additional_emails || [];
    }
    if (JSON.stringify(form.additional_phones || []) !== JSON.stringify(it.additional_phones || [])) {
      payload.additional_phones = form.additional_phones || [];
    }
    if (form.linkedin_url !== (it.linkedin_url || '')) payload.linkedin_url = form.linkedin_url;
    if (form.website !== (it.website || '')) payload.website = form.website;
    if (form.references_text !== (it.references_text || '')) payload.references_text = form.references_text;
    if (JSON.stringify(form.active_languages || []) !== JSON.stringify(it.active_languages || [])) {
      payload.active_languages = form.active_languages || [];
    }
    if (JSON.stringify(form.passive_languages || []) !== JSON.stringify(it.passive_languages || [])) {
      payload.passive_languages = form.passive_languages || [];
    }
    if (JSON.stringify(form.modalities || []) !== JSON.stringify(it.modalities || [])) {
      payload.modalities = form.modalities || [];
    }
    if (Object.keys(payload).length === 0) {
      setError('No has cambiado nada todavía.');
      setStatus('error');
      setSaving(false);
      return;
    }
    if (maxSpecialties > 0 && payload.specialty !== undefined) {
      const cnt = countSpecialties(payload.specialty);
      if (cnt > maxSpecialties) {
        setError(`Puedes registrar máximo ${maxSpecialties} especialidades (tienes ${cnt}). Quita algunas para guardar.`);
        setStatus('error');
        setSaving(false);
        return;
      }
    }
    try {
      const { data: result } = await api.put('/me/interpreter', payload);
      setLastSaveResult(result || null);
      setStatus('success');
      await load();
      // Only switch to "requests" tab if something was actually sent for review.
      if (result?.pending_request_id) setActiveTab('requests');
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setStatus('error');
    } finally { setSaving(false); }
  }

  async function cancelRequest(id) {
    if (!window.confirm('¿Cancelar esta solicitud pendiente?')) return;
    try {
      await api.delete(`/me/profile-edits/${id}`);
      await load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  }

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center pt-20"><div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>;
  if (!dash || !dash.interpreter) return <div className="min-h-[60vh] flex flex-col items-center justify-center pt-32 px-4" data-testid="no-profile"><UserCircle size={48} className="text-charcoal-300 mb-4" /><p className="text-charcoal-500">No tienes un perfil de intérprete asociado.</p></div>;

  const it = dash.interpreter;
  const pendingRequests = requests.filter((r) => r.status === 'pending');
  const recentDecisions = requests.filter((r) => r.status !== 'pending').slice(0, 5);
  const completeness = dash.completeness || { percent: 0, missing: [] };
  const membership = dash.membership || { status: 'unset' };
  const unread = dash.unread || {};

  return (
    <>
      {/* Premium gradient hero — matches the internal Forum visual language */}
      <div data-testid="interpreter-dashboard-hero" className="relative overflow-hidden bg-gradient-to-br from-cmic-900 via-cmic-800 to-slate-900 text-white pt-28 pb-20">
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-cmic-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-10 w-72 h-72 bg-cmic-400/10 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md ring-1 ring-white/15 px-3 py-1 rounded-full text-xs font-medium mb-4">
            <UserCircle size={12} /> Portal del miembro
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2">
            {t.portal.welcome}, {user?.full_name || ''}
          </h1>
          <p className="text-white/70 max-w-2xl leading-relaxed">
            Gestiona tu perfil profesional, revisa el estado de tu membresía y mantente al día con el Colegio.
          </p>
        </div>
      </div>
      <section className="pb-12 bg-gray-50 min-h-screen" data-testid="interpreter-dashboard">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 -mt-10 relative z-10">

          {/* Membership status banner — only when dues display is enabled */}
          {showDues && <MembershipBanner membership={membership} />}

          {/* Top stats: completeness + quicklinks */}
          <div className={`grid gap-4 ${maintenance ? 'lg:grid-cols-1' : 'lg:grid-cols-3'}`}>
            <CompletenessCard completeness={completeness} onComplete={() => setActiveTab('edit')} />
            {!maintenance && publicCfg?.forum_enabled !== false && (
              <QuickLinkCard
                to={`/${t.locale}/foro`}
                testid="member-link-forum"
                icon={MessageSquare}
                color="cmic"
                title="Interno"
                subtitle="Discusiones entre miembros"
                badge={unread.forum}
              />
            )}
            {!maintenance && (
              <QuickLinkCard
                to={`/${t.locale}/${t.locale === 'es' ? 'noticias' : 'news'}`}
                testid="member-link-news"
                icon={Newspaper}
                color="amber"
                title="Noticias"
                subtitle="Públicas y privadas"
                badge={unread.news}
              />
            )}
          </div>

          {/* Public profile preview link */}
          <Link
            to={`/${t.locale}/${t.locale === 'es' ? 'directorio' : 'directory'}/${it.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="view-public-profile"
            className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-2xl p-4 hover:border-cmic-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Eye size={18} />
              </div>
              <div>
                <div className="text-sm font-semibold text-charcoal-800">Ver mi perfil público</div>
                <div className="text-xs text-charcoal-500">Así te ve la comunidad y los clientes</div>
              </div>
            </div>
            <ExternalLink size={16} className="text-charcoal-400 group-hover:text-cmic-500" />
          </Link>

          {/* Security questions link */}
          <Link
            to={`/${t.locale}/${t.locale === 'es' ? 'mi-perfil' : 'my-profile'}/${t.locale === 'es' ? 'seguridad' : 'security'}`}
            data-testid="link-security-questions"
            className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-2xl p-4 hover:border-cmic-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cmic-50 text-cmic-600 flex items-center justify-center">
                <ShieldCheck size={18} />
              </div>
              <div>
                <div className="text-sm font-semibold text-charcoal-800">Preguntas de seguridad</div>
                <div className="text-xs text-charcoal-500">Para recuperar tu cuenta si pierdes acceso al correo</div>
              </div>
            </div>
            <ArrowRight size={16} className="text-charcoal-400 group-hover:text-cmic-500" />
          </Link>

          {/* Change password link */}
          <Link
            to="/cambiar-contrasena"
            data-testid="link-change-password"
            className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-2xl p-4 hover:border-cmic-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <KeyRound size={18} />
              </div>
              <div>
                <div className="text-sm font-semibold text-charcoal-800">Cambiar contraseña</div>
                <div className="text-xs text-charcoal-500">Actualiza la contraseña de tu cuenta del CMIC</div>
              </div>
            </div>
            <ArrowRight size={16} className="text-charcoal-400 group-hover:text-cmic-500" />
          </Link>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="p-3 sm:p-4 border-b border-gray-100">
              <div className="bg-slate-100 p-1.5 rounded-2xl inline-flex w-full sm:w-auto overflow-x-auto gap-1">
                <TabBtn id="overview" active={activeTab} setActive={setActiveTab} testid="tab-overview" label="Resumen" />
                <TabBtn id="edit" active={activeTab} setActive={setActiveTab} testid="tab-edit" label="Editar perfil" />
                <TabBtn
                  id="requests"
                  active={activeTab}
                  setActive={setActiveTab}
                  testid="tab-requests"
                  label="Mis solicitudes"
                  badge={pendingRequests.length}
                />
                <TabBtn id="support" active={activeTab} setActive={setActiveTab} testid="tab-support" label="Soporte" badge={supportUnread} />
                <TabBtn id="manual" active={activeTab} setActive={setActiveTab} testid="tab-manual" label="Manual de usuario" />
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {activeTab === 'overview' && (
                <OverviewTab dash={dash} it={it} t={t} hideInternal={maintenance} forumEnabled={publicCfg?.forum_enabled !== false} />
              )}

              {activeTab === 'edit' && (
                <div className="space-y-6">
                  <DemoChecklist it={it} form={form} onGo={() => {}} />
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <ApprovalLegend />

                    {/* Nombre — 3 bloques separados (orden mexicano), editables */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre(s)</label>
                        <input
                          value={form.nombres || ''}
                          onChange={(e) => setForm({ ...form, nombres: e.target.value })}
                          data-testid="profile-field-nombres"
                          placeholder="Ej. María Elena"
                          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Apellido paterno</label>
                        <input
                          value={form.apellido_paterno || ''}
                          onChange={(e) => setForm({ ...form, apellido_paterno: e.target.value })}
                          data-testid="profile-field-apellido-paterno"
                          placeholder="Ej. Lasso de la Vega"
                          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Apellido materno</label>
                        <input
                          value={form.apellido_materno || ''}
                          onChange={(e) => setForm({ ...form, apellido_materno: e.target.value })}
                          data-testid="profile-field-apellido-materno"
                          placeholder="Puede ir vacío"
                          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                        />
                        <p className="text-[10px] text-charcoal-400 mt-1">Tu nombre se publica al instante en el directorio.</p>
                      </div>
                    </div>

                    {/* Sección: Foto y contacto (instantáneo) */}
                    <EditSection
                      icon={Phone}
                      title="Foto y datos de contacto"
                      hint="Estos cambios se publican al instante en tu perfil público."
                      tone="auto"
                      testid="edit-section-contact"
                    >
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Foto de perfil</label>
                          <ImageUploader
                            value={form.photo_url || ''}
                            onChange={(url) => setForm({ ...form, photo_url: url })}
                            testid="profile-photo-uploader"
                            shape="circle"
                          />
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Correo principal</label>
                            <input
                              type="email"
                              value={form.email || ''}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              data-testid="profile-field-email"
                              placeholder="tu-correo@ejemplo.com"
                              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                            />
                            <p className="text-[10px] text-charcoal-400 mt-1">Correo de contacto que aparece en tu perfil del directorio.</p>
                          </div>
                          <Field label="Ciudad / país" name="city" form={form} setForm={setForm} />
                          <Field label="Teléfono principal" name="phone" form={form} setForm={setForm} />
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Correos adicionales</label>
                            <ChipInput value={form.additional_emails || []} onChange={(v) => setForm({ ...form, additional_emails: v })} placeholder="otro@correo.com (Enter)" testid="me-additional-emails" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Teléfonos adicionales</label>
                            <ChipInput value={form.additional_phones || []} onChange={(v) => setForm({ ...form, additional_phones: v })} placeholder="+52 … (Enter)" testid="me-additional-phones" />
                          </div>
                          <Field label="LinkedIn URL" name="linkedin_url" form={form} setForm={setForm} placeholder="https://linkedin.com/in/…" />
                          <Field label="Página web" name="website" form={form} setForm={setForm} placeholder="https://tu-sitio.com" />
                        </div>
                      </div>
                    </EditSection>

                    {/* Sección: Idiomas (requiere aprobación) */}
                    <EditSection
                      icon={Globe}
                      title="Idiomas"
                      hint="Cambios en tus idiomas activos y pasivos los revisa el consejo directivo antes de publicarse."
                      tone="approval"
                      testid="edit-section-languages"
                    >
                      <div>
                        <label className="text-xs font-medium text-charcoal-600 mb-1.5 flex items-center gap-2">
                          Idiomas activos (interpreta HACIA)
                          <ApprovalChip />
                        </label>
                        <ChipInput
                          value={form.active_languages || []}
                          onChange={(v) => setForm({ ...form, active_languages: v })}
                          placeholder="Ej: Español, Inglés (Enter para añadir)"
                          testid="profile-field-active_languages"
                          palette="cmic"
                          suggestions={publicCfg?.languages_catalog || []}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-charcoal-600 mb-1.5 flex items-center gap-2">
                          Idiomas pasivos (interpreta DESDE)
                          <ApprovalChip />
                        </label>
                        <ChipInput
                          value={form.passive_languages || []}
                          onChange={(v) => setForm({ ...form, passive_languages: v })}
                          placeholder="Ej: Francés, Portugués"
                          testid="profile-field-passive_languages"
                          palette="gray"
                          suggestions={publicCfg?.languages_catalog || []}
                        />
                      </div>
                    </EditSection>

                    {/* Sección: Especialidades y modalidades (requiere aprobación) */}
                    <EditSection
                      icon={Briefcase}
                      title="Especialidades y modalidades"
                      hint="Define las áreas en las que te especializas y cómo ofreces tu interpretación. Requiere aprobación del consejo."
                      tone="approval"
                      testid="edit-section-expertise"
                    >
                      <div>
                        <label className="text-xs font-medium text-charcoal-600 mb-1.5 flex items-center gap-2">
                          Especialidades
                          <ApprovalChip />
                          {maxSpecialties > 0 && (() => {
                            const count = countSpecialties(form.specialty);
                            const over = count > maxSpecialties;
                            return (
                              <span data-testid="specialty-counter" className={`ml-auto text-[11px] font-semibold tabular-nums ${over ? 'text-rose-600' : 'text-charcoal-400'}`}>
                                {count}/{maxSpecialties}
                              </span>
                            );
                          })()}
                        </label>
                        <ChipInput
                          value={form.specialty || []}
                          onChange={(v) => setForm({ ...form, specialty: v })}
                          placeholder="Selecciona o escribe una especialidad…"
                          testid="profile-field-specialty"
                          palette="gray"
                          suggestions={publicCfg?.specialties_catalog || []}
                        />
                        {maxSpecialties > 0 && (() => {
                          const count = countSpecialties(form.specialty);
                          return count > maxSpecialties ? (
                            <p data-testid="specialty-limit-warning" className="mt-1 text-[11px] text-rose-600">
                              Puedes registrar máximo {maxSpecialties} especialidades. Quita {count - maxSpecialties} para poder guardar.
                            </p>
                          ) : (
                            <p className="mt-1 text-[11px] text-charcoal-400">Selecciona de las existentes o escribe para crear una nueva. Máximo {maxSpecialties} especialidades.</p>
                          );
                        })()}
                        {maxSpecialties === 0 && (
                          <p className="mt-1 text-[11px] text-charcoal-400">Selecciona de las existentes o escribe para crear una nueva.</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-charcoal-600 mb-1.5 flex items-center gap-2">
                          Modalidades de interpretación
                          <ApprovalChip />
                        </label>
                        <ChipInput
                          value={form.modalities || []}
                          onChange={(v) => setForm({ ...form, modalities: v })}
                          placeholder="Ej: Simultánea presencial, Remota (RSI)…"
                          testid="profile-field-modalities"
                          palette="gray"
                          suggestions={publicCfg?.modalities_catalog || []}
                        />
                        <p className="text-[10px] text-charcoal-400 mt-1">Simultánea presencial, RSI (remota), Enlace, Susurrada, Consecutiva u otras.</p>
                      </div>
                    </EditSection>

                    {/* Sección: Semblanza y referencias (instantáneo) */}
                    <EditSection
                      icon={FileText}
                      title="Semblanza y referencias"
                      hint="Cuenta tu trayectoria. Estos cambios se publican al instante."
                      tone="auto"
                      testid="edit-section-bio"
                    >
                      <div>
                        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Semblanza</label>
                        <RichTextEditor
                          value={form.bio || ''}
                          onChange={(html) => setForm({ ...form, bio: html })}
                          placeholder="Cuenta tu trayectoria profesional…"
                          testid="profile-field-bio"
                          minHeight={200}
                          allowMedia={false}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Certificaciones</label>
                        <CertificationsField
                          value={form.references_text || ''}
                          onChange={(v) => setForm({ ...form, references_text: v })}
                        />
                        <p className="mt-1.5 text-[11px] text-charcoal-400">Escribe una certificación por casilla. Se mostrarán numeradas (1, 2, 3…) en tu perfil público.</p>
                      </div>
                    </EditSection>

                    {status === 'success' && (
                      <div className="space-y-2" data-testid="profile-save-success">
                        {lastSaveResult?.auto_applied?.length > 0 && (
                          <div className="flex items-start gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 p-3 rounded-xl">
                            <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="font-semibold mb-0.5">Cambios aplicados al instante</p>
                              <p className="text-emerald-600">{formatFieldNames(lastSaveResult.auto_applied)} ya están visibles en tu perfil público.</p>
                            </div>
                          </div>
                        )}
                        {lastSaveResult?.pending_request_id && (
                          <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-xl">
                            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="font-semibold mb-0.5">Pendiente de aprobación</p>
                              <p className="text-amber-700">{formatFieldNames(lastSaveResult.pending_fields || [])} {(lastSaveResult.pending_fields || []).length === 1 ? 'requiere' : 'requieren'} aprobación del consejo directivo. Te notificaremos cuando sea revisado.</p>
                            </div>
                          </div>
                        )}
                        {!lastSaveResult?.auto_applied?.length && !lastSaveResult?.pending_request_id && (
                          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 p-3 rounded-xl">
                            <CheckCircle2 size={18} />
                            <span className="text-sm font-medium">Cambios guardados.</span>
                          </div>
                        )}
                      </div>
                    )}
                    {status === 'error' && (
                      <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 p-3 rounded-xl" data-testid="profile-save-error">
                        <AlertCircle size={18} />
                        <span className="text-sm font-medium">{error || 'Error'}</span>
                      </div>
                    )}

                    <div className="sticky bottom-4 sm:static z-10 flex flex-wrap gap-3 items-center pt-2">
                      <button
                        type="submit"
                        disabled={saving}
                        data-testid="profile-save-btn"
                        className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3 transition-all duration-200 disabled:opacity-60 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                      >
                        {saving ? (
                          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enviando…</>
                        ) : (
                          <><Send size={18} />Guardar cambios</>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('overview')}
                        className="text-sm text-charcoal-500 hover:text-charcoal-700"
                      >Cancelar</button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'requests' && (
                <RequestsTab
                  pendingRequests={pendingRequests}
                  recentDecisions={recentDecisions}
                  onCancel={cancelRequest}
                  onEditMore={() => setActiveTab('edit')}
                />
              )}

              {activeTab === 'support' && (
                <SupportTab onRead={refreshSupportUnread} />
              )}

              {activeTab === 'manual' && (
                <UserManual forumEnabled={publicCfg?.forum_enabled !== false} />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const SUPPORT_HELP_OPTIONS = [
  { id: 'login', label: 'No puedo iniciar sesión' },
  { id: 'photo', label: 'No puedo subir mi foto' },
  { id: 'profile', label: 'No sé cómo llenar o guardar mi perfil' },
  { id: 'other', label: 'Otro / no estoy seguro' },
];

function SupportTab({ onRead }) {
  const [list, setList] = useState(null);
  const [view, setView] = useState('list'); // list | new | thread
  const [activeToken, setActiveToken] = useState(null);
  const [msg, setMsg] = useState('');
  const [help, setHelp] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      const { data } = await api.get('/support/my-tickets');
      setList(data || []);
    } catch { setList([]); }
    onRead && onRead();
  }
  useEffect(() => { load(); }, []);

  function toggleHelp(id) {
    setHelp((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  async function create(e) {
    e.preventDefault();
    if (!msg.trim()) return;
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/support/my-tickets', { message: msg.trim(), help_types: help, source: 'member' });
      setMsg(''); setHelp([]);
      await load();
      setActiveToken(data.thread_token);
      setView('thread');
    } catch {
      setError('No pudimos enviar tu solicitud. Inténtalo de nuevo.');
    }
    setCreating(false);
  }

  if (view === 'thread' && activeToken) {
    return (
      <div data-testid="support-tab">
        <button onClick={() => { setView('list'); load(); }} data-testid="support-back-list" className="inline-flex items-center gap-1.5 text-sm font-medium text-cmic-600 hover:text-cmic-700 mb-4">
          <ChevronLeft size={16} /> Volver a mis conversaciones
        </button>
        <SupportThread token={activeToken} onUpdated={load} />
      </div>
    );
  }

  if (view === 'new') {
    return (
      <div data-testid="support-tab">
        <button onClick={() => setView('list')} className="inline-flex items-center gap-1.5 text-sm font-medium text-cmic-600 hover:text-cmic-700 mb-4">
          <ChevronLeft size={16} /> Volver
        </button>
        <h3 className="text-lg font-semibold text-charcoal-900 mb-1">Nueva solicitud de soporte</h3>
        <p className="text-sm text-charcoal-500 mb-5">Cuéntanos en qué necesitas ayuda y te responderemos aquí mismo.</p>
        <form onSubmit={create} className="space-y-4 max-w-xl">
          <div>
            <label className="block text-xs font-semibold text-charcoal-700 mb-2">¿Con qué necesitas ayuda? (opcional)</label>
            <div className="space-y-2">
              {SUPPORT_HELP_OPTIONS.map((opt) => (
                <label key={opt.id} className="flex items-center gap-2.5 text-sm text-charcoal-700 cursor-pointer">
                  <input type="checkbox" checked={help.includes(opt.id)} onChange={() => toggleHelp(opt.id)} data-testid={`support-new-help-${opt.id}`} className="rounded border-gray-300 text-cmic-600 focus:ring-cmic-500" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">Escribe tu mensaje</label>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} data-testid="support-new-message" placeholder="Describe tu problema o duda…" className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 resize-y" />
          </div>
          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{error}</div>}
          <button type="submit" disabled={creating || !msg.trim()} data-testid="support-new-submit" className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3 text-sm disabled:opacity-50">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Enviar solicitud</>}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="support-tab">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2"><LifeBuoy size={20} className="text-cmic-600" /> Soporte</h3>
          <p className="text-sm text-charcoal-500 mt-0.5">Tus conversaciones con el equipo de soporte.</p>
        </div>
        <button onClick={() => { setView('new'); setError(''); }} data-testid="support-new-request-btn" className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-4 py-2.5 text-sm">
          <Plus size={16} /> Nueva solicitud
        </button>
      </div>

      {list === null ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-cmic-500" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-14 bg-slate-50 rounded-2xl border border-gray-100" data-testid="support-empty-member">
          <LifeBuoy size={36} className="text-charcoal-300 mx-auto mb-3" />
          <p className="text-charcoal-500 mb-4">Aún no tienes solicitudes de soporte.</p>
          <button onClick={() => setView('new')} className="inline-flex items-center gap-2 text-cmic-600 hover:text-cmic-700 font-semibold text-sm">
            <Plus size={15} /> Crear mi primera solicitud
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((t) => (
            <button
              key={t.thread_token}
              onClick={() => { setActiveToken(t.thread_token); setView('thread'); }}
              data-testid={`support-my-ticket-${t.thread_token}`}
              className="w-full text-left bg-white border border-gray-200 hover:border-cmic-300 rounded-2xl p-4 transition-colors"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="inline-flex items-center gap-2">
                  {t.unread && <span className="w-2.5 h-2.5 rounded-full bg-rose-500" title="Nueva respuesta" data-testid="support-ticket-unread-dot" />}
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border ${t.status === 'resolved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {t.status === 'resolved' ? 'Resuelto' : 'Abierto'}
                  </span>
                  {t.unread && <span className="text-[11px] font-semibold text-rose-600">Nueva respuesta</span>}
                </span>
                <span className="text-xs text-charcoal-400 inline-flex items-center gap-1"><Clock size={12} />{new Date(t.created_at).toLocaleDateString('es-MX')}</span>
              </div>
              {t.last_message && (
                <p className="text-sm text-charcoal-600 line-clamp-2">
                  <span className="font-semibold text-charcoal-800">{t.last_message.from === 'admin' ? 'Soporte: ' : 'Tú: '}</span>
                  {t.last_message.text}
                </p>
              )}
              <span className="inline-flex items-center gap-1 text-xs text-cmic-600 font-medium mt-2"><MessageSquare size={12} /> Ver conversación</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TabBtn({ id, active, setActive, label, badge, testid }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => setActive(id)}
      data-testid={testid}
      className={`relative flex-1 sm:flex-none rounded-xl px-4 sm:px-5 py-2.5 text-sm font-semibold transition-all duration-300 whitespace-nowrap ${isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/50'}`}
    >
      {label}
      {badge > 0 && (
        <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">{badge}</span>
      )}
    </button>
  );
}

/* ── Edit form building blocks (demo-week redesign) ───────────────────────── */

// A grouped, titled card for the edit form. `tone` controls the accent:
//   'auto'     → green accent (publishes instantly)
//   'approval' → amber accent (needs board approval)
function EditSection({ icon: Icon, title, hint, tone = 'auto', testid, children }) {
  const isApproval = tone === 'approval';
  const accent = isApproval
    ? { ring: 'border-amber-200', head: 'bg-amber-50/60', icon: 'bg-amber-100 text-amber-700' }
    : { ring: 'border-emerald-200', head: 'bg-emerald-50/50', icon: 'bg-emerald-100 text-emerald-700' };
  return (
    <section data-testid={testid} className={`rounded-2xl border ${accent.ring} overflow-hidden shadow-sm`}>
      <div className={`px-5 sm:px-6 py-4 border-b ${accent.ring} ${accent.head} flex items-start justify-between gap-3`}>
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${accent.icon}`}>
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-charcoal-800 leading-tight">{title}</h3>
            {hint && <p className="text-xs text-charcoal-500 mt-0.5 leading-snug">{hint}</p>}
          </div>
        </div>
        <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0 ${isApproval ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {isApproval ? <><ShieldCheck size={11} /> Requiere aprobación</> : <><CheckCircle2 size={11} /> Instantáneo</>}
        </span>
      </div>
      <div className="p-5 sm:p-6 space-y-5 bg-white">{children}</div>
    </section>
  );
}

function ApprovalLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-charcoal-600 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3" data-testid="approval-legend">
      <span className="font-medium text-charcoal-700">Cómo se publican tus cambios:</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Instantáneo</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Requiere aprobación del consejo</span>
    </div>
  );
}

function ApprovalChip() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full" title="Cambios en este campo requieren aprobación del consejo directivo">
      <ShieldCheck size={10} /> Requiere aprobación
    </span>
  );
}

// Demo-week validation checklist: at-a-glance review of the key fields the
// member should confirm before the directory goes public.
function DemoChecklist({ it, form }) {
  const has = (v) => (Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim()));
  const items = [
    { label: 'Foto de perfil', ok: has(form.photo_url) },
    { label: 'Ciudad / país', ok: has(form.city) },
    { label: 'Teléfono', ok: has(form.phone) },
    { label: 'Idiomas activos', ok: has(form.active_languages) },
    { label: 'Idiomas pasivos', ok: has(form.passive_languages) },
    { label: 'Especialidades', ok: has(form.specialty) },
    { label: 'Modalidades', ok: has(form.modalities) },
    { label: 'Semblanza', ok: has(form.bio) },
  ];
  const done = items.filter((i) => i.ok).length;
  return (
    <div className="rounded-2xl border border-cmic-200 bg-gradient-to-br from-cmic-50 to-white p-5 sm:p-6" data-testid="demo-checklist">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-cmic-600 text-white flex items-center justify-center flex-shrink-0">
          <ListChecks size={20} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-charcoal-800 leading-tight">Revisa tus datos para la semana demo</h3>
          <p className="text-xs text-charcoal-500 mt-0.5">Confirma que cada campo sea correcto. Has completado <strong className="text-cmic-700">{done} de {items.length}</strong>.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((i) => (
          <div key={i.label} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${i.ok ? 'bg-emerald-50/60 border-emerald-100 text-charcoal-700' : 'bg-amber-50/60 border-amber-100 text-charcoal-700'}`} data-testid={`checklist-${i.ok ? 'done' : 'todo'}`}>
            {i.ok ? <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" /> : <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />}
            <span className="truncate">{i.label}</span>
            {!i.ok && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-amber-600">Falta</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function MembershipBanner({ membership }) {
  const status = membership?.status;
  let cfg;
  switch (status) {
    case 'current':
      cfg = {
        bg: 'bg-emerald-50 border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-700',
        Icon: ShieldCheck,
        title: 'Membresía al corriente',
        subtitle: `Vigente hasta el ${membership.paid_until} · ${membership.days_left} día${membership.days_left === 1 ? '' : 's'} restante${membership.days_left === 1 ? '' : 's'}`,
      };
      break;
    case 'expired':
      cfg = {
        bg: 'bg-rose-50 border-rose-200',
        iconBg: 'bg-rose-100 text-rose-700',
        Icon: AlertTriangle,
        title: 'Membresía vencida',
        subtitle: `Tu cuota venció el ${membership.paid_until}. Tu perfil ya no aparece en el directorio público.`,
      };
      break;
    case 'suspended':
      cfg = {
        bg: 'bg-gray-100 border-gray-300',
        iconBg: 'bg-gray-200 text-gray-700',
        Icon: AlertTriangle,
        title: 'Cuenta suspendida',
        subtitle: 'Contacta al consejo directivo para reactivar tu perfil.',
      };
      break;
    case 'unset':
      cfg = {
        bg: 'bg-amber-50 border-amber-200',
        iconBg: 'bg-amber-100 text-amber-700',
        Icon: Clock,
        title: 'Cuota sin registrar',
        subtitle: 'Si ya pagaste, contacta al consejo directivo para que registre tu pago.',
      };
      break;
    default:
      return null;
  }
  return (
    <div data-testid="membership-banner" className={`flex items-start gap-4 border rounded-2xl p-5 ${cfg.bg}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.iconBg}`}>
        <cfg.Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-charcoal-800 flex items-center gap-2 flex-wrap">
          <span>{cfg.title}</span>
          {membership.category_label && status !== 'suspended' && (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-white/70 text-charcoal-600 border border-charcoal-200 rounded px-1.5 py-0.5">
              {membership.category_label}
            </span>
          )}
        </div>
        <div className="text-sm text-charcoal-600 mt-0.5">{cfg.subtitle}</div>
        {(membership.expected_amount > 0 || membership.amount_paid > 0) && (
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs" data-testid="membership-amounts">
            <AmountChip label="Cuota anual" value={membership.expected_amount} testid="member-expected" tone="neutral" />
            <AmountChip label="Pagado" value={membership.amount_paid} testid="member-paid" tone="emerald" />
            <AmountChip
              label={membership.amount_owed > 0 ? 'Adeudo' : 'Sin adeudo'}
              value={membership.amount_owed}
              testid="member-owed"
              tone={membership.amount_owed > 0 ? 'amber' : 'emerald'}
            />
          </div>
        )}
        {membership.amount_owed > 0 && membership.payment_commitment_date && (
          <div className="mt-2 text-[12px] text-charcoal-600" data-testid="member-commitment">
            Compromiso de pago: <strong>{membership.payment_commitment_date}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

function AmountChip({ label, value, testid, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-white/70 text-charcoal-700 border-charcoal-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
  };
  const fmt = Number(value || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="font-bold tabular-nums">{fmt}</div>
    </div>
  );
}

function CompletenessCard({ completeness, onComplete }) {
  const pct = completeness?.percent || 0;
  const missing = completeness?.missing || [];
  const barColor = pct >= 80 ? 'from-emerald-500 to-emerald-600' : pct >= 50 ? 'from-amber-500 to-amber-600' : 'from-rose-500 to-rose-600';
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="completeness-card">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={16} className="text-cmic-600" />
        <h3 className="text-sm font-semibold text-charcoal-800">Tu perfil está al {pct}%</h3>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div className={`h-full bg-gradient-to-r ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {missing.length > 0 ? (
        <>
          <p className="text-xs text-charcoal-500 mb-2">Te faltan: <span className="font-medium text-charcoal-700">{missing.slice(0, 3).join(', ')}{missing.length > 3 ? '…' : ''}</span></p>
          <button
            onClick={onComplete}
            data-testid="completeness-complete-btn"
            className="text-xs font-medium text-cmic-600 hover:text-cmic-700 inline-flex items-center gap-1"
          >
            <Sparkles size={12} />Completar ahora <ArrowRight size={12} />
          </button>
        </>
      ) : (
        <p className="text-xs text-emerald-600 font-medium">¡Perfil completo! 🎉</p>
      )}
    </div>
  );
}

function QuickLinkCard({ to, testid, icon: Icon, color, title, subtitle, badge }) {
  const colors = {
    cmic: 'bg-cmic-50 text-cmic-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
  };
  return (
    <Link to={to} data-testid={testid} className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-cmic-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center relative ${colors[color] || colors.cmic}`}>
        <Icon size={20} />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">{badge}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-charcoal-800">{title}</div>
        <div className="text-xs text-charcoal-500">{subtitle}</div>
      </div>
      <ArrowRight size={16} className="text-charcoal-400 group-hover:text-cmic-500 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}

function OverviewTab({ dash, it, t, hideInternal, forumEnabled = true }) {
  const recentThreads = dash.recent_threads || [];
  const latestNews = dash.latest_news || [];
  const latestEvents = dash.latest_events || [];
  if (hideInternal) {
    // Maintenance mode: hide all internal/community sections. The member only
    // sees an invitation to review/update their own profile.
    return (
      <div className="space-y-6" data-testid="overview-tab">
        <div className="bg-gradient-to-br from-cmic-50 to-cmic-100/40 border border-cmic-100 rounded-2xl p-6 text-center" data-testid="overview-maintenance-notice">
          <Sparkles size={22} className="text-cmic-600 mx-auto mb-2" />
          <h4 className="text-base font-semibold text-cmic-800 mb-1">Revisa y completa tu perfil</h4>
          <p className="text-sm text-cmic-700 mb-4 max-w-md mx-auto">El sitio está en preparación. Por ahora puedes revisar tu información y ver cómo se verá tu perfil público.</p>
          <button
            onClick={() => document.querySelector('[data-testid="tab-edit"]')?.click()}
            data-testid="overview-go-edit"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-cmic-600 hover:bg-cmic-700 rounded-xl px-5 py-2.5"
          >
            <Sparkles size={14} />Editar mi perfil <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className={`grid gap-4 ${forumEnabled ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        {forumEnabled && (
        <InfoCard icon={MessageSquare} title="Foro · Hilos recientes" testid="overview-forum-list" to={`/${t.locale}/foro`}>
          {recentThreads.length === 0 ? (
            <p className="text-xs text-charcoal-400 py-4 text-center">Sin actividad reciente.</p>
          ) : (
            <ul className="divide-y divide-gray-100 -mx-1">
              {recentThreads.slice(0, 4).map((th) => (
                <li key={th.id} className="py-2 px-1">
                  <Link to={`/${t.locale}/foro/t/${th.id}`} className="block hover:bg-gray-50 -mx-1 px-1 rounded">
                    <div className="text-sm font-medium text-charcoal-800 truncate">{th.title}</div>
                    <div className="text-[11px] text-charcoal-500">{(th.reply_count || 0)} respuestas · {timeAgo(th.last_activity_at)}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </InfoCard>
        )}
        <InfoCard icon={Newspaper} title="Últimas noticias" testid="overview-news-list" to={`/${t.locale}/${t.locale === 'es' ? 'noticias' : 'news'}`}>
          {latestNews.length === 0 ? (
            <p className="text-xs text-charcoal-400 py-4 text-center">Sin noticias.</p>
          ) : (
            <ul className="divide-y divide-gray-100 -mx-1">
              {latestNews.slice(0, 4).map((n) => (
                <li key={n.id} className="py-2 px-1">
                  <div className="text-sm font-medium text-charcoal-800 truncate">{n.title}</div>
                  <div className="text-[11px] text-charcoal-500 flex items-center gap-1.5">
                    {n.is_private && <span className="text-fuchsia-600 font-medium">PRIVADA</span>}
                    <span>{timeAgo(n.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </InfoCard>
      </div>

      <InfoCard icon={Calendar} title="Próximos eventos" testid="overview-events-list" to={`/${t.locale}/${t.locale === 'es' ? 'eventos' : 'events'}`}>
        {latestEvents.length === 0 ? (
          <p className="text-xs text-charcoal-400 py-4 text-center">No hay eventos próximos.</p>
        ) : (
          <ul className="divide-y divide-gray-100 -mx-1">
            {latestEvents.slice(0, 4).map((ev) => (
              <li key={ev.id} className="py-2 px-1">
                <div className="text-sm font-medium text-charcoal-800 truncate">{ev.title}</div>
                <div className="text-[11px] text-charcoal-500">
                  {ev.is_private && <span className="text-fuchsia-600 font-medium mr-1.5">PRIVADO</span>}
                  {ev.starts_at ? new Date(ev.starts_at).toLocaleString('es-MX') : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </InfoCard>

      <div className="bg-gradient-to-br from-cmic-50 to-cmic-100/40 border border-cmic-100 rounded-2xl p-5">
        <h4 className="text-sm font-semibold text-cmic-800 mb-1">¿Quieres actualizar tu información?</h4>
        <p className="text-xs text-cmic-700 mb-3">Cambia tu foto, biografía, idiomas o datos de contacto. Tus cambios serán revisados por el consejo directivo antes de publicarse.</p>
        <Link
          to="#"
          onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="tab-edit"]')?.click(); }}
          data-testid="overview-go-edit"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-cmic-700 hover:text-cmic-800"
        >
          <Sparkles size={14} />Editar mi perfil <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, testid, to, children }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={15} className="text-charcoal-600" />
          <h3 className="text-sm font-semibold text-charcoal-800">{title}</h3>
        </div>
        {to && (
          <Link to={to} className="text-xs text-cmic-600 hover:text-cmic-700 font-medium inline-flex items-center gap-1">
            Ver todo <ArrowRight size={11} />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function RequestsTab({ pendingRequests, recentDecisions, onCancel, onEditMore }) {
  return (
    <div className="space-y-6" data-testid="requests-tab">
      <div data-testid="pending-requests-section">
        <h3 className="text-sm font-semibold text-charcoal-800 mb-3 flex items-center gap-2">
          <Clock size={14} />Pendientes de aprobación
          <span className="text-[10px] text-charcoal-400 font-normal">({pendingRequests.length})</span>
        </h3>
        {pendingRequests.length === 0 ? (
          <p className="text-sm text-charcoal-500 bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
            No tienes solicitudes pendientes.
          </p>
        ) : (
          <ul className="space-y-2">
            {pendingRequests.map((r) => (
              <li key={r.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid={`pending-request-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-amber-900 mb-1">{new Date(r.created_at).toLocaleString('es-MX')}</div>
                    <div className="text-sm text-charcoal-700">
                      Campos solicitados: <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-amber-200">{Object.keys(r.changes || {}).join(', ')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onCancel(r.id)}
                    title="Cancelar solicitud"
                    data-testid={`cancel-request-${r.id}`}
                    className="p-2 rounded-lg text-amber-700 hover:bg-amber-100 flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid="recent-decisions-section">
        <h3 className="text-sm font-semibold text-charcoal-800 mb-3">Decisiones recientes</h3>
        {recentDecisions.length === 0 ? (
          <p className="text-xs text-charcoal-400 py-4 text-center">No hay decisiones recientes.</p>
        ) : (
          <ul className="space-y-2">
            {recentDecisions.map((r) => (
              <li key={r.id} className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3 text-sm">
                <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${r.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                  {r.status === 'approved' ? <Check size={10} /> : <X size={10} />}
                  {r.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-charcoal-700">
                    {Object.keys(r.changes || {}).join(', ')}
                  </div>
                  <div className="text-[10px] text-charcoal-400 mt-0.5">{new Date(r.reviewed_at || r.updated_at).toLocaleString('es-MX')}</div>
                  {r.admin_notes && <div className="text-[11px] text-charcoal-600 mt-1 bg-gray-50 px-2 py-1 rounded">Notas: {r.admin_notes}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        onClick={onEditMore}
        data-testid="requests-go-edit"
        className="inline-flex items-center gap-2 text-sm font-medium text-cmic-600 hover:text-cmic-700"
      >
        <Sparkles size={14} />Hacer otra solicitud <ArrowRight size={14} />
      </button>
    </div>
  );
}

function Field({ label, name, form, setForm, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-charcoal-600 mb-1.5">{label}</label>
      <input
        type="text"
        value={form[name] || ''}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        placeholder={placeholder}
        data-testid={`profile-field-${name}`}
        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
      />
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `hace ${days}d`;
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}
